/**
 * useFloorPicture — the store ↔ static-plane bridge.
 *
 * Memoizes ONE SkPicture from the current document. The dependency list IS
 * the re-record contract (see the floor-plan ADR):
 * - `revision`     — committed content change
 * - `lod`          — zoom bucket change (set on camera settle)
 * - `cullBounds`   — cull window change (set on camera settle)
 * - `selectedKey`  — selected objects move to the DYNAMIC plane, so the
 *                    static picture must re-record without them
 * - `theme`/`font` — appearance inputs
 *
 * The camera itself is deliberately ABSENT — pan/zoom never re-record.
 * Object data is read imperatively via `getState()` inside the memo (the
 * `revision` dep is the change signal), so no wide store subscription
 * exists and unrelated state changes never re-run this hook.
 *
 * @module floorplan/hooks/useFloorPicture
 * @category FloorPlan
 */

import { useMemo } from 'react';
import type { SkPicture } from '@shopify/react-native-skia';

import type { Bounds } from '../models/geometry';
import { recordFloorPicture } from '../renderer/recordPicture';
import type { RenderContext } from '../renderer/types';
import {
  selectRenderOrderedObjects,
  useFloorEngineStore,
} from '../stores/useFloorEngineStore';
import { boundsUnion } from '../utils/bounds';
import { cullObjects } from '../utils/culling';
import type { FloorThemeResult } from './useFloorTheme';

export type UseFloorPictureOptions = {
  /**
   * Exclude the current selection from the recording (default true — the
   * dynamic plane draws selected objects). The read-only POS live floor
   * passes false so selection never forces a re-record.
   */
  excludeSelected?: boolean;
};

/**
 * Module-level builder (this tree is compiler-excluded). `revisionSignal`
 * exists purely as the change key for the imperative `getState()` read —
 * threading it as a PARAMETER keeps the `useMemo` dependency genuinely
 * consumed (the `react-hooks/exhaustive-deps` contract) without widening
 * the store subscription.
 */
const buildStaticPicture = (
  revisionSignal: number,
  hasPlan: boolean,
  cullBounds: Bounds | null,
  ctx: RenderContext,
  selectedKey: string,
  excludeSelected: boolean,
): SkPicture | null => {
  if (!hasPlan || revisionSignal < 0) return null;
  const state = useFloorEngineStore.getState();
  const meta = state.meta;
  if (meta === null) return null;

  const ordered = selectRenderOrderedObjects(state);
  const culled = cullObjects(ordered, cullBounds);
  let drawable = culled;
  if (excludeSelected && selectedKey.length > 0) {
    const selected = new Set(selectedKey.split('|'));
    drawable = culled.filter((object) => !selected.has(object.id));
  }

  const planBounds: Bounds = {
    x: 0,
    y: 0,
    width: meta.width,
    height: meta.height,
  };
  const recordBounds =
    cullBounds === null ? planBounds : boundsUnion(planBounds, cullBounds);
  return recordFloorPicture(drawable, ctx, recordBounds);
};

const useFloorPicture = (
  appearance: FloorThemeResult,
  options?: UseFloorPictureOptions,
): SkPicture | null => {
  const excludeSelected = options?.excludeSelected ?? true;
  const revision = useFloorEngineStore((s) => s.revision);
  const lod = useFloorEngineStore((s) => s.lod);
  const cullBounds = useFloorEngineStore((s) => s.cullBounds);
  const hasPlan = useFloorEngineStore((s) => s.meta !== null);
  // Stable string key — a Set/array would be a new reference per render.
  const selectedKey = useFloorEngineStore((s) =>
    excludeSelected ? s.selectedIds.join('|') : '',
  );
  const { theme, font } = appearance;

  return useMemo(
    () =>
      buildStaticPicture(
        revision,
        hasPlan,
        cullBounds,
        { lod, theme, font },
        selectedKey,
        excludeSelected,
      ),
    [
      revision,
      hasPlan,
      cullBounds,
      lod,
      theme,
      font,
      selectedKey,
      excludeSelected,
    ],
  );
};

export default useFloorPicture;
